/**
 * React Hook для работы с аутентификацией в FastAPI backend v2.0
 * 
 * Использует React Query для кеширования и управления состоянием
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { authApi } from "@/services/auth-v2.service";
import { hasToken, removeToken } from "@/lib/api-client-v2";
import type { LoginRequest, RegisterRequest, User } from "@/types/api-v2";

const USER_QUERY_KEY = ["auth", "me"];
const PERMISSIONS_QUERY_KEY = ["auth", "permissions"];

/**
 * Hook для получения текущего пользователя
 */
export function useUser() {
  return useQuery<User | null>({
    queryKey: USER_QUERY_KEY,
    queryFn: async () => {
      // Если нет токена, сразу возвращаем null
      if (!hasToken()) {
        return null;
      }

      try {
        return await authApi.me();
      } catch (error) {
        // Если ошибка 401, токен невалидный - удаляем
        console.error("Failed to get user:", error);
        return null;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 минут
  });
}

/**
 * Hook для входа
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authApi.login(credentials),
    onSuccess: async () => {
      // Получаем данные пользователя после успешного входа
      const user = await authApi.me();
      queryClient.setQueryData(USER_QUERY_KEY, user);
      
      // Загружаем разрешения пользователя
      try {
        const permissions = await authApi.permissions();
        queryClient.setQueryData(PERMISSIONS_QUERY_KEY, permissions.permissions);
      } catch (error) {
        console.error("Failed to load permissions:", error);
      }
      
      // Редирект на главную
      navigate("/");
    },
    onError: (error: Error) => {
      console.error("Login failed:", error);
    },
  });
}

/**
 * Hook для регистрации
 */
export function useRegister() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
    onSuccess: async () => {
      // Получаем данные пользователя после успешной регистрации
      const userData = await authApi.me();
      queryClient.setQueryData(USER_QUERY_KEY, userData);
      
      // Загружаем разрешения пользователя
      try {
        const permissions = await authApi.permissions();
        queryClient.setQueryData(PERMISSIONS_QUERY_KEY, permissions.permissions);
      } catch (error) {
        console.error("Failed to load permissions:", error);
      }
      
      // Редирект на главную
      navigate("/");
    },
    onError: (error: Error) => {
      console.error("Registration failed:", error);
    },
  });
}

/**
 * Hook для выхода
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      // Очищаем кэш пользователя
      queryClient.setQueryData(USER_QUERY_KEY, null);
      queryClient.clear();
      
      // Удаляем токен (на всякий случай, authApi.logout уже удаляет)
      removeToken();
      
      // Редирект на страницу входа
      navigate("/login");
    },
  });
}

/**
 * Hook для обновления токена
 */
export function useRefreshToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.refresh(),
    onSuccess: async () => {
      // Обновляем данные пользователя
      const user = await authApi.me();
      queryClient.setQueryData(USER_QUERY_KEY, user);
    },
  });
}

/**
 * Hook для получения разрешений текущего пользователя
 */
export function usePermissions() {
  return useQuery<string[]>({
    queryKey: PERMISSIONS_QUERY_KEY,
    queryFn: async () => {
      // Если нет токена, возвращаем пустой массив
      if (!hasToken()) {
        return [];
      }

      try {
        const response = await authApi.permissions();
        return response.permissions;
      } catch (error) {
        // Если ошибка 401, токен невалидный - возвращаем пустой массив
        console.error("Failed to get permissions:", error);
        return [];
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 минут
    enabled: hasToken(), // Загружаем только если есть токен
  });
}

/**
 * Комплексный hook для аутентификации с проверкой прав
 */
export function useAuth() {
  const { data: user, isLoading, error } = useUser();
  const { data: permissions = [], isLoading: isLoadingPermissions } = usePermissions();
  const login = useLogin();
  const register = useRegister();
  const logout = useLogout();
  const refresh = useRefreshToken();
  const queryClient = useQueryClient();

  // Функции проверки прав
  const hasPermission = (permission: string): boolean => {
    if (!permissions || permissions.length === 0) {
      return false;
    }
    // Администраторы с admin.access имеют доступ ко всему
    if (permissions.includes("admin.access")) {
      return true;
    }
    return permissions.includes(permission);
  };

  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    if (!permissions || permissions.length === 0) {
      return false;
    }
    // Администраторы с admin.access имеют доступ ко всему
    if (permissions.includes("admin.access")) {
      return true;
    }
    return requiredPermissions.some(perm => permissions.includes(perm));
  };

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    if (!permissions || permissions.length === 0) {
      return false;
    }
    // Администраторы с admin.access имеют доступ ко всему
    if (permissions.includes("admin.access")) {
      return true;
    }
    return requiredPermissions.every(perm => permissions.includes(perm));
  };

  return {
    // Состояние
    user,
    isAuthenticated: !!user,
    isLoading: isLoading || isLoadingPermissions,
    error,
    permissions,

    // Методы проверки прав
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Методы
    login: login.mutate,
    loginAsync: login.mutateAsync,
    isLoggingIn: login.isPending,
    loginError: login.error,

    register: register.mutate,
    registerAsync: register.mutateAsync,
    isRegistering: register.isPending,
    registerError: register.error,

    logout: logout.mutate,
    isLoggingOut: logout.isPending,

    refresh: refresh.mutate,
    refreshAsync: async () => {
      const result = await refresh.mutateAsync();
      // Обновляем разрешения после обновления токена
      try {
        const permissions = await authApi.permissions();
        queryClient.setQueryData(PERMISSIONS_QUERY_KEY, permissions.permissions);
      } catch (error) {
        console.error("Failed to update permissions:", error);
      }
      return result;
    },
  };
}

